import cn from 'classnames';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { CSSProperties, useContext, useEffect } from 'react';

import { ModalContext } from '~components/ModalProvider';
import Bubble from '~components/ui/Bubble';
import Modal from '~components/ui/Modal';
import QRWindow from '~components/ui/QRWindow';
import go from '~utils/go';
import useAuthUser from '~utils/hooks/useAuthUser';
import useGlobalState from '~utils/hooks/useGlobalState';
import useLocalStorage from '~utils/hooks/useLocalStorage';
import useMembership from '~utils/hooks/useMembership';
import useToggleState from '~utils/hooks/useToggleState';

import { Buy } from '~types';

import s from './BubbleStack.module.css';

const SignUpFlow = dynamic(() => import('~components/User/SignUpFlow'), {
  ssr: false,
}) as any;

const BubbleStack = () => {
  const { asPath } = useRouter();
  const user = useAuthUser();
  const { data: membership } = useMembership();
  const context = useContext(ModalContext);
  const [buysVisibles, setBuysVisibles] = useGlobalState('buysToggler', true);
  const [showQRWindow, toggleQRWindow] = useToggleState(false);
  const [localBuys, setLocalBuys] = useLocalStorage<Buy[]>('Buys', []);

  const toggleBuysVisibles = () => setBuysVisibles(!buysVisibles);

  const sortedBuys = localBuys.sort((a, b) => dayjs(a.schedule).unix() - dayjs(b.schedule).unix());

  useEffect(() => {
    const filtered = sortedBuys.filter((item) =>
      dayjs(item.schedule).isAfter(dayjs().subtract(40, 'minutes')),
    );
    if (filtered.length < localBuys.length) {
      setLocalBuys(filtered);
    } else if (sortedBuys.length) {
      if (buysVisibles) setTimeout(() => setBuysVisibles(false), 1000);
      const onScroll = () => {
        setBuysVisibles(false);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', onScroll);
      };
    }
  }, [localBuys]);

  let position = sortedBuys.length + 1;
  // Se quita de la compra
  if (asPath.includes('/comprar-boletos')) return null;
  return (
    <>
      <div
        className={cn(s.root, { [s.visible]: buysVisibles })}
        onClick={toggleBuysVisibles}
        onMouseEnter={toggleBuysVisibles}
        onMouseLeave={toggleBuysVisibles}
      >
        {sortedBuys.map((buy) => {
          position--;
          // @ts-ignore
          const style: CSSProperties = { '--position': `${position}` };
          return (
            <Link
              href={`/compra/${buy.confirmationNumber}`}
              key={buy.id}
              className={s.buy}
              style={style}
            >
              {/* @ts-ignore */}

              <Bubble className={s.buyBubble} image={buy.Movie?.poster?.url} />
            </Link>
          );
        })}
        {!!user ? (
          !asPath.includes('/perfil') ? (
            <Bubble
              className="bg-secondary"
              onClick={() => {
                if ((sortedBuys.length && buysVisibles) || !sortedBuys.length) {
                  if (user?.Profile?.loyaltyMembershipId && !!membership) {
                    toggleQRWindow();
                  } else if (
                    !membership ||
                    (!user?.Profile?.loyaltyCard && !user?.Profile?.virtualLoyaltyCard) ||
                    !user?.Profile?.loyaltyMembershipId
                  ) {
                    window.location.hash = 'membershipActivation';
                    context.setModal('signup');
                  }
                }
              }}
              image={
                <span className={s.qrCitiPremia}>
                  <Image src="/img/QRExample.png" fill alt="QR CitiPremia" />
                </span>
              }
            />
          ) : null
        ) : (
          <Bubble
            className="bg-primary"
            onClick={() => {
              if ((sortedBuys.length && buysVisibles) || !sortedBuys.length) {
                go('/beneficios-citipremia');
              }
            }}
            image={
              <span className={s.citiPremiaLogo}>
                <Image
                  src="/img/citiid-2.png"
                  className="object-contain"
                  fill
                  alt="CitiPremia"
                  sizes="(min-width: 768px) 80px, 56px"
                  priority
                />
              </span>
            }
          />
        )}
      </div>
      <div
        className={s.overlay}
        onTouchStart={() => setBuysVisibles(false)}
        onMouseDown={() => setBuysVisibles(false)}
      />
      <Modal open={showQRWindow} onClose={toggleQRWindow}>
        <QRWindow loyaltyNumber={`${user?.Profile?.loyaltyCard}`} />
      </Modal>
      {context.modal === 'signup' && (
        <Modal open onClose={() => context.setModal(null)}>
          <SignUpFlow email={context?.data?.email} onSuccess={() => context.setModal(null)} />
        </Modal>
      )}
    </>
  );
};

export type BubbleStackType = typeof BubbleStack;

export default BubbleStack;
